import GameSignals from '@/signals/GameSignals'
import { BaseChildState } from '@stateMachine/BaseChildState'
import {
    getPendingHoldAndWinJackpotPopupTiers,
    HoldAndWinJackpotTier,
} from '@/containers/reels/BonusRespinBoardContainer'

/**
 * Runs after the H&W collect animation and before the End Popup.
 * Jackpot popups are normally played inline during collect (per jackpot coin /
 * full-board mini). This state only shows tiers that were not already played.
 */
export class HoldAndWinJackpotPopupState extends BaseChildState {
    // Per-popup budget used only for the safety fallback (never the normal completion path).
    private static readonly FALLBACK_PER_POPUP_MS = 6000
    private static readonly FALLBACK_BASE_MS = 3000

    private _isWaitingPopups: boolean = false
    private _fallbackTimeoutId: number | null = null

    public canTrigger(): boolean {
        const isHoldAndWinActive = Boolean(this._model.spinState.isHoldAndWinActive)
        const spinType = String(this._model.spinData?.spin_type || '').toLowerCase()
        const nextRoundType = String(this._model.spinData?.next_round_type || '').toLowerCase()
        const respinsLeft = Number(this._model.spinData?.respins_left ?? 0)
        const hasRespinsLeft =
            (nextRoundType === 'respins' || nextRoundType === 'respin') && Number.isFinite(respinsLeft) && respinsLeft > 0

        // Same gate as HoldAndWinSummaryState: only on the final respin of an active H&W session.
        if (!isHoldAndWinActive) {
            return false
        }

        if (!(spinType === 'respin' && !hasRespinsLeft)) {
            return false
        }

        // Skip entirely when every jackpot popup already ran during collect.
        return getPendingHoldAndWinJackpotPopupTiers().length > 0
    }

    public begin(): void {
        const popupTiers: HoldAndWinJackpotTier[] = getPendingHoldAndWinJackpotPopupTiers()
        if (popupTiers.length === 0) {
            this.end()
            return
        }

        this._isWaitingPopups = true
        ;(GameSignals as any).jackpotPopupComplete?.connect(this.onPopupsComplete)
        ;(GameSignals as any).jackpotPopupStart?.emit(popupTiers)

        const fallbackMs =
            HoldAndWinJackpotPopupState.FALLBACK_BASE_MS +
            popupTiers.length * HoldAndWinJackpotPopupState.FALLBACK_PER_POPUP_MS
        this._fallbackTimeoutId = window.setTimeout(() => {
            this.onPopupsComplete()
        }, fallbackMs)
    }

    private onPopupsComplete = (): void => {
        if (!this._isWaitingPopups) {
            return
        }

        this._isWaitingPopups = false

        if (this._fallbackTimeoutId !== null) {
            window.clearTimeout(this._fallbackTimeoutId)
            this._fallbackTimeoutId = null
        }

        ;(GameSignals as any).jackpotPopupComplete?.disconnect(this.onPopupsComplete)
        this.end()
    }

    public end = (): void => {
        this._parent.endChild()
    }

    public cleanUp(): void {
        this._isWaitingPopups = false

        if (this._fallbackTimeoutId !== null) {
            window.clearTimeout(this._fallbackTimeoutId)
            this._fallbackTimeoutId = null
        }

        ;(GameSignals as any).jackpotPopupComplete?.disconnect(this.onPopupsComplete)
    }
}
